import React, { Component, type ComponentClass, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { bindActionCreators } from "redux";
import { type InjectedFormProps, reduxForm } from "redux-form";
import { MerchantGroupDetailViewWithActionCreators } from "./MerchantGroupDetailView";
import MerchantGroupsView from "./MerchantGroupsView";
import { EditMerchantGroup } from "src/merchantgroups/actions/MerchantGroupActionCreators";
import { MerchantGroupEdit } from "src/merchantgroups/formatters/MerchantGroupFormatters";
import {
  type IMerchantGroupData,
  IMerchantGroupDataToMerchantGroup,
  MerchantGroupToIMerchantGroupData,
} from "src/merchantgroups/forms/IMerchantGroupData";
import { MerchantGroupForm } from "src/merchantgroups/forms/MerchantGroupForm";
import type IMerchantGroupURLParams from "src/merchantgroups/objects/IMerchantGroupURLParams";
import type MerchantGroup from "src/merchantgroups/objects/MerchantGroup";
import { getMerchantGroupDetailViewPath } from "src/merchantgroups/views/MerchantGroupPaths";
import Loader from "src/shared/components/Loader";

export const EDIT_MERCHANT_GROUP_VIEW_PATH: string =
  "/mg/:merchantGroupUUID/edit";

export function GetEditMerchantGroupViewPath(
  merchantGroup: MerchantGroup,
): string {
  return EDIT_MERCHANT_GROUP_VIEW_PATH.replace(
    ":merchantGroupUUID",
    merchantGroup.merchantGroupUUID,
  );
}

// Group Data
export interface IEditMerchantGroupData extends IMerchantGroupData {}

interface IEditMerchantGroupViewInnerProps
  extends IEditMerchantGroupViewWithReduxProps,
    InjectedFormProps<
      IEditMerchantGroupData,
      IEditMerchantGroupViewWithReduxProps
    > {}

interface IEditMerchantGroupViewInnerState {
  editMerchantGroupPromise?: Promise<{}>;
}

class EditMerchantGroupViewInner extends Component<
  IEditMerchantGroupViewInnerProps,
  IEditMerchantGroupViewInnerState
> {
  public state = {
    editMerchantGroupPromise: undefined,
  };

  constructor(props: IEditMerchantGroupViewInnerProps) {
    super(props);

    this.onSubmit = this.onSubmit.bind(this);
    this.onPopupAbort = this.onPopupAbort.bind(this);
  }

  public async onSubmit(formData: IEditMerchantGroupData): Promise<void> {
    const merchantGroup: MerchantGroup = IMerchantGroupDataToMerchantGroup(
      formData,
      this.props.merchantGroup.merchantGroupUUID,
    );

    const { EditMerchantGroup } = this.props;
    const editMerchantGroupPromise: Promise<{}> =
      EditMerchantGroup(merchantGroup);
    this.setState({
      editMerchantGroupPromise,
    });
    await editMerchantGroupPromise;

    // Reload
    const { onLoad } = this.props;
    onLoad();

    // Redirect to MerchantGroups
    const { history } = this.props;
    const merchantGroupViewPath: string =
      getMerchantGroupDetailViewPath(merchantGroup);
    history.push(merchantGroupViewPath);
  }

  public onPopupAbort(): void {
    const { merchantGroup } = this.props;
    this.props.history.push(getMerchantGroupDetailViewPath(merchantGroup));
  }

  public render(): ReactNode {
    const { merchantGroup, form, handleSubmit, pristine, submitting, invalid } =
      this.props;
    const { editMerchantGroupPromise } = this.state;
    return (
      <Modal isOpen toggle={this.onPopupAbort}>
        <ModalHeader toggle={this.onPopupAbort}>
          <MerchantGroupEdit>{merchantGroup}</MerchantGroupEdit>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(this.onSubmit)}>
            <MerchantGroupForm
              form={form}
              nextButtonDisabled={submitting || invalid}
              merchantGroup={merchantGroup}
            >
              <Button
                type="submit"
                color="primary"
                disabled={pristine || submitting || invalid}
              >
                <FormattedMessage id="merchant_groups.edit.submit" />
              </Button>
            </MerchantGroupForm>
            {editMerchantGroupPromise && (
              <Loader promise={editMerchantGroupPromise} />
            )}
          </form>
        </ModalBody>
      </Modal>
    );
  }
}

// Redux Form
interface IEditMerchantGroupViewWithReduxProps
  extends IEditMerchantGroupViewWithActionCreatorsProps {
  EditMerchantGroup: (merchantGroup: MerchantGroup) => Promise<{}>;
}

const EditMerchantGroupViewWithRedux: ComponentClass<IEditMerchantGroupViewWithReduxProps> =
  reduxForm<IEditMerchantGroupData, IEditMerchantGroupViewWithReduxProps>({
    form: "EditMerchantGroupView",
  })(EditMerchantGroupViewInner);

// Redux Connect
interface IEditMerchantGroupViewWithActionCreatorsProps
  extends IMerchantGroupEditViewProps {
  onLoad: () => void;
  merchantGroup: MerchantGroup;
}

const EditMerchantGroupViewWithActionCreators = connect(
  (state, props: IEditMerchantGroupViewWithActionCreatorsProps) => {
    const { merchantGroup } = props;
    return {
      initialValues: MerchantGroupToIMerchantGroupData(merchantGroup),
    };
  },
  (dispatch) =>
    bindActionCreators(
      {
        EditMerchantGroup,
      },
      dispatch,
    ),
)(EditMerchantGroupViewWithRedux);

// Export Default
export interface IMerchantGroupEditViewProps
  extends RouteComponentProps<IMerchantGroupURLParams> {}

export default (props: IMerchantGroupEditViewProps) => {
  return (
    <MerchantGroupsView {...props}>
      {(merchantGroups: MerchantGroup[], onLoad: () => void) =>
        React.createElement(MerchantGroupDetailViewWithActionCreators, {
          ...props,
          onLoad,
          children: (merchantGroup: MerchantGroup, onLoad: () => void) =>
            React.createElement(EditMerchantGroupViewWithActionCreators, {
              ...props,
              merchantGroup,
              onLoad,
            }),
        })
      }
    </MerchantGroupsView>
  );
};
